import type { TimelineEntry, TimelineThreadMessage } from '@shared/schemas/timeline-entry';
import type { EntitySignalRow } from '@shared/flow/entity-signals';
import { fields, type DetailField } from '../actions/action-fields';
import { fmtDateOnlyText, dateOnlyDay, BARE_DATE } from './accountFieldFormat';

/**
 * Pure row/bucket/badge logic for the account timeline (U1e, plan 005 Wave
 * 0) — lifted out of `AccountTimeline.tsx` so both V1's `AccountTimeline`
 * and U8a's V2 `AccountTimelineTab` share ONE row model, ONE sort, and ONE
 * bucketing/expand-all algebra rather than forking it. React-free,
 * Glaze-free: every export here is a plain function or data structure, no
 * JSX. `AccountTimeline.tsx` imports these back and supplies the rendering.
 */

export type AccountEntryKind = TimelineEntry['kind'];

/**
 * A pill groups one or more entry kinds under a single filter chip. Every
 * kind belongs to exactly one group; `workspace_created`/`workspace_join`
 * share the `workspace` group so they filter and count together per the
 * plan ("ONE 'Workspace' filter pill — the chip matches either kind").
 */
export type FilterGroup = 'task' | 'event' | 'signal' | 'workspace';

/**
 * Sibling chip vocabulary for the mode selector (plan §2b item 5, the
 * account twin of `ContactTimeline`'s `Mode`): every `FilterGroup` chip PLUS
 * `'action'`. `'action'` is deliberately NOT in `KIND_TO_GROUP` — action rows
 * come from a separate boundary (`useFlowAccountSignals`) and are not wire
 * entries, even though (U6) they render as rows in the SAME accordion. The
 * chip is a filter over that merged list: `null` → everything, a group chip →
 * that group's wire entries only, `'action'` → action rows only.
 */
export type Mode = FilterGroup | 'action';

export const KIND_TO_GROUP: Record<AccountEntryKind, FilterGroup> = {
  task: 'task',
  event: 'event',
  signal: 'signal',
  workspace_created: 'workspace',
  workspace_join: 'workspace',
};

export const GROUP_LABEL: Record<Mode, string> = {
  task: 'Tasks',
  event: 'Events',
  signal: 'Signals',
  workspace: 'Workspace',
  action: 'Actions',
};

export function bucketLabel(bucket: string): string {
  if (bucket === 'upcoming') return 'Upcoming & Overdue';
  if (bucket === 'undated') return 'Undated';
  const d = new Date(`${bucket}-01T00:00:00Z`);
  return isNaN(d.getTime()) ? bucket : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function bucketOrder(bucket: string): string {
  // upcoming first, months descending, undated last.
  if (bucket === 'upcoming') return '2';
  if (bucket === 'undated') return '0';
  return `1-${bucket}`;
}

export function fmtDay(iso: string | null): string {
  if (!iso) return '—';
  // A bare YYYY-MM-DD is a DATE (the due_date fallback), not an instant.
  if (BARE_DATE.test(iso)) return fmtDateOnlyText(iso, { month: 'short', day: 'numeric' }) ?? '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Drop empty parts, join the rest — for one-line subtitles. */
function join(...parts: (string | null | undefined)[]): string | null {
  const kept = parts.filter((p): p is string => !!p && p.trim() !== '');
  return kept.length ? kept.join(' · ') : null;
}

/**
 * Display bucket for a merged ACTION row — the ONE client-side bucketing in
 * this file (see `AccountTimeline.tsx`'s header comment for why it is not a
 * violation of the "server is the bucketing authority" contract: a
 * `terra_signals` row never passes through the timeline endpoint, so
 * nothing server-side ever buckets it).
 *
 * Mirrors `bucketForDate` in `src/services/account-timeline.ts` (~line 169)
 * exactly — `>= now` → `'upcoming'`, else `'YYYY-MM'` in UTC, null/invalid →
 * `'undated'`. Keep the two in step if that rule ever changes. (The server's
 * extra `pinUpcoming` argument is an `account_activity` open-task rule with
 * no analogue here.)
 */
export function actionBucket(iso: string | null, now: Date): string {
  if (!iso) return 'undated';
  const at = Date.parse(iso);
  if (isNaN(at)) return 'undated';
  if (at >= now.getTime()) return 'upcoming';
  const d = new Date(at);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * One rendered accordion row: either a server-bucketed WIRE entry or a
 * client-bucketed merged ACTION row. Both carry the fields the bucketing and
 * ordering passes need (`bucket`, `effectiveAt`, `key`) so the render loop
 * never branches on the source to place a row — only to draw it.
 */
export type TimelineRow =
  | { rowKind: 'entry'; key: string; bucket: string; effectiveAt: string | null; entry: TimelineEntry }
  | { rowKind: 'action'; key: string; bucket: string; effectiveAt: string | null; signalRow: EntitySignalRow };

/**
 * Workspace row title. Create rows: "Workspace created · {name}" (name
 * omitted when null). Join rows: "{contactName} joined {name}" — but
 * `contactName` is frequently null (unmatched contact, or a create row's
 * Clay_User__c creator id that can never resolve to a Contact — see the
 * schema doc), so this renders gracefully with a generic subject instead of
 * a blank/undefined title.
 */
function workspaceTitle(e: Extract<TimelineEntry, { kind: 'workspace_created' | 'workspace_join' }>): string {
  const workspace = e.workspaceName ?? 'a workspace';
  if (e.kind === 'workspace_created') {
    return e.workspaceName ? `Workspace created · ${e.workspaceName}` : 'Workspace created';
  }
  return e.contactName ? `${e.contactName} joined ${workspace}` : `Someone joined ${workspace}`;
}

/**
 * Row title per entry kind. Signals title on `eventName ?? name`; tasks and
 * events on `subject`, with a kind-name placeholder when untitled.
 *
 * A COLLAPSED EMAIL THREAD (U8) titles on `threadSubject` instead — the
 * server's normalized form, with the `[Gong In]`/`[Gong Out]` prefix and
 * every accumulated `Re:`/`Fwd:` already stripped. Deliberately NOT
 * re-derived here: the server owns subject normalization (same posture as
 * `bucket`), and the raw per-message subjects stay visible in the expanded
 * member list.
 */
export function entryTitle(e: TimelineEntry): string {
  if (e.kind === 'signal') return e.eventName ?? e.name ?? 'Signal';
  if (e.kind === 'workspace_created' || e.kind === 'workspace_join') return workspaceTitle(e);
  if (e.kind === 'task' && e.threadSubject) return e.threadSubject;
  return e.subject ?? (e.kind === 'task' ? 'Task' : 'Event');
}

/** Secondary "· a · b · c" segments per entry kind (falsy entries dropped). */
function entryMeta(e: TimelineEntry): Array<string | null> {
  if (e.kind === 'signal') return [e.signalType, e.signalSubtype];
  if (e.kind === 'workspace_join') return [e.relationshipType];
  if (e.kind === 'workspace_created') return [];
  return [e.kind === 'task' ? e.status : e.type, e.ownerName, e.contactName];
}

/**
 * Trailing "· {meta}" text for a row. Falls back to the raw `kind` string
 * for task/event/signal (existing U6 behavior, unchanged) — but workspace
 * rows already say everything they need to in the title (`entryTitle`), so
 * a bare "workspace_created" slug would just be UI noise; they fall back to
 * empty instead, which hides the trailing segment entirely (see the render
 * call site).
 */
export function entryMetaText(e: TimelineEntry): string {
  const meta = entryMeta(e).filter(Boolean).join(' · ');
  if (meta) return meta;
  if (e.kind === 'workspace_created' || e.kind === 'workspace_join') return '';
  return e.kind;
}

/** Per-kind expanded detail fields (label/value rows). */
export function entryFields(e: TimelineEntry): DetailField[] {
  if (e.kind === 'task') {
    return fields(
      ['Status', e.status],
      ['Priority', e.priority],
      ['Type', join(e.type, e.subtype)],
      ['Owner', e.ownerName],
      ['Contact', e.contactName],
      ['Due', e.activityDate],
    );
  }
  if (e.kind === 'event') {
    return fields(
      ['Type', join(e.type, e.subtype)],
      ['Location', e.location],
      ['All Day', e.isAllDay ? 'Yes' : null],
      ['Owner', e.ownerName],
      ['Contact', e.contactName],
      ['Attendees', e.attendees.length
        ? e.attendees.map(a => a.name).filter(Boolean).join(', ') || `${e.attendees.length}`
        : null],
    );
  }
  if (e.kind === 'workspace_created') {
    return fields(['Workspace', e.workspaceName], ['Contact', e.contactName]);
  }
  if (e.kind === 'workspace_join') {
    return fields(
      ['Workspace', e.workspaceName],
      ['Relationship', e.relationshipType],
      ['Contact', e.contactName],
    );
  }
  // signal
  return fields(['Type', e.signalType], ['Subtype', e.signalSubtype]);
}

/** Free-text description, per kind — workspace rows carry none (no wire
 *  field), unlike task/event/signal. */
export function entryDescription(e: TimelineEntry): string | null {
  if (e.kind === 'workspace_created' || e.kind === 'workspace_join') return null;
  return e.description;
}

/** `workspaceId` for the two workspace kinds; `null` for every other kind. */
export function workspaceIdOf(e: TimelineEntry): string | null {
  if (e.kind === 'workspace_created' || e.kind === 'workspace_join') return e.workspaceId;
  return null;
}

/** Footer copy shown under a truncated (server-capped) timeline. Verbatim
 *  prod text — a constant so both `AccountTimeline` and any future V2 tab
 *  render the identical string. */
export const ACCOUNT_TIMELINE_TRUNCATED_COPY = 'Showing the most recent activity';

/**
 * Counts are per PILL GROUP, not per wire kind — `workspace_created` and
 * `workspace_join` both roll up into the single "Workspace" count/pill.
 * Only wire entries are counted; merged action rows have no group (they
 * have their own separate `'action'` chip, counted directly from the
 * signals-fetch length at the call site).
 */
export function countByGroup(entries: TimelineEntry[]): Record<FilterGroup, number> {
  const c: Record<FilterGroup, number> = { task: 0, event: 0, signal: 0, workspace: 0 };
  for (const e of entries) c[KIND_TO_GROUP[e.kind]] += 1;
  return c;
}

/**
 * The ONE merged row list: server-bucketed wire entries + client-bucketed
 * action rows (`actionBucket`), sorted `effectiveAt` DESC with undated rows
 * last — the same comparator the server's own merge uses
 * (`src/services/account-timeline.ts`'s `mergeEntries`).
 *
 * `Array.prototype.sort` is stable, and the wire entries already arrive
 * `effectiveAt DESC` from the server, so this pass only SLOTS action rows
 * into that existing order; it never re-orders wire entries against each
 * other (same-timestamp ties keep their server sequence), and because entry
 * rows are concatenated before action rows, an equal-date entry/action tie
 * always keeps the entry first.
 */
export function buildTimelineRows(
  entries: TimelineEntry[],
  signalRows: EntitySignalRow[],
  now: Date,
): TimelineRow[] {
  const entryRows: TimelineRow[] = entries.map(e => ({
    rowKind: 'entry', key: `${e.kind}-${e.id}`, bucket: e.bucket, effectiveAt: e.effectiveAt, entry: e,
  }));
  const actionRows: TimelineRow[] = signalRows.map(row => {
    // `event_date ?? due_date` — the same date a merged action row sorts on
    // in `ContactTimeline`'s `normalize`.
    const effectiveAt = row.event_date ?? dateOnlyDay(row.due_date);
    return {
      rowKind: 'action', key: `act-${row.signal_id}`, bucket: actionBucket(effectiveAt, now), effectiveAt, signalRow: row,
    };
  });
  return [...entryRows, ...actionRows].sort((a, b) => {
    if (a.effectiveAt === b.effectiveAt) return 0;
    if (a.effectiveAt === null) return 1;
    if (b.effectiveAt === null) return -1;
    return a.effectiveAt < b.effectiveAt ? 1 : -1;
  });
}

/**
 * Chip semantics mirror the contact side exactly: no chip → the whole
 * merged list; a group chip → that group's wire entries only; the Actions
 * chip → action rows only.
 */
export function visibleRows(rows: TimelineRow[], mode: Mode | null): TimelineRow[] {
  return rows.filter(r => {
    if (mode === null) return true;
    if (mode === 'action') return r.rowKind === 'action';
    return r.rowKind === 'entry' && KIND_TO_GROUP[r.entry.kind] === mode;
  });
}

/** Group rows by bucket, ordered: upcoming → months desc → undated. */
export function bucketize(rows: TimelineRow[]): Array<[string, TimelineRow[]]> {
  const buckets = new Map<string, TimelineRow[]>();
  for (const r of rows) {
    const list = buckets.get(r.bucket) ?? [];
    list.push(r);
    buckets.set(r.bucket, list);
  }
  return [...buckets.entries()].sort((a, b) => bucketOrder(b[0]).localeCompare(bucketOrder(a[0])));
}

/**
 * Whether every currently-visible row is expanded. Requires at least one
 * visible row so an empty filter result doesn't read as "already all
 * expanded".
 */
export function allExpanded(expandedKeys: Set<string>, visibleKeys: string[]): boolean {
  return visibleKeys.length > 0 && visibleKeys.every(k => expandedKeys.has(k));
}

/**
 * Expand-all/collapse-all toggle (2026-08-28, Spencer) — scoped to the
 * currently VISIBLE (filtered) keys, mirroring how the pill counts are also
 * filter-scoped. Union/subtract against the *visible* keys only — never
 * replaces the whole set. A row expanded while a different filter was
 * active must stay expanded when that filter is cleared, even though it
 * wasn't part of this toggle's visible scope. Never mutates `prev`.
 */
export function toggleExpandAll(prev: Set<string>, visibleKeys: string[]): Set<string> {
  if (allExpanded(prev, visibleKeys)) {
    const next = new Set(prev);
    for (const k of visibleKeys) next.delete(k);
    return next;
  }
  return new Set([...prev, ...visibleKeys]);
}

/**
 * "×N synced rows" badge DATA (U4, Bucket A/E plan) — the Gong fan-out
 * badge. Gong's Salesforce sync writes ONE Task/Event record per attendee
 * for a single real meeting, all sharing `Gong__Gong_Activity_Id__c`.
 * `buildAccountTimeline` collapses those rows server-side into one entry
 * carrying `syncedRowCount`; this is the only place that fact surfaces to
 * the rep. Returns `null` at `syncedRowCount <= 1` (or when the field is
 * absent — pre-U4 wire shape) so an ordinary un-fanned-out row stays clean.
 *
 * Title copy is the prototype's verbatim
 * (docs/flow/bucket-a-account-view-prototype.html:877) — do not paraphrase.
 */
export function syncedRowsBadge(
  entry: Extract<TimelineEntry, { kind: 'task' | 'event' }>,
): { count: number; title: string } | null {
  const count = entry.syncedRowCount ?? 1;
  if (!entry.gongActivityId || count <= 1) return null;
  return {
    count,
    title: `${count} Salesforce ${entry.kind} rows share Gong__Gong_Activity_Id__c ${entry.gongActivityId} — Gong logs one row per attendee. Grouped into one entry.`,
  };
}

/**
 * "×N emails · ↑out ↓in" badge DATA (U8, Bucket A/E plan) — the collapsed
 * email-thread badge. `syncedRowCount` counts duplicate SALESFORCE ROWS of
 * one message; `threadCount` counts distinct MESSAGES of a conversation —
 * the two are orthogonal and can both appear on one row.
 *
 * Direction is null on hand-logged emails carrying neither Gong prefix, so
 * `outbound + inbound` need not sum to `count` — the aria label says so
 * rather than implying a mix that isn't there. Returns `null` when
 * `threadCount` is absent/zero — the server only ever emits thread fields
 * on a real ≥ 2 chain, so absence IS "not a thread".
 */
export function threadBadge(
  entry: Extract<TimelineEntry, { kind: 'task' }>,
): { count: number; outbound: number; inbound: number; ariaLabel: string } | null {
  const count = entry.threadCount;
  if (!count) return null;
  const messages: TimelineThreadMessage[] = entry.threadMessages ?? [];
  const outbound = messages.filter(m => m.direction === 'outbound').length;
  const inbound = messages.filter(m => m.direction === 'inbound').length;
  const mixParts = [
    outbound ? `${outbound} sent` : null,
    inbound ? `${inbound} received` : null,
  ].filter(Boolean);
  const ariaLabel = mixParts.length
    ? `${count} emails in this thread — ${mixParts.join(', ')}`
    : `${count} emails in this thread`;
  return { count, outbound, inbound, ariaLabel };
}
